<template>
  <b-card>
    <b-overlay :show="isLoading" rounded="sm">
      <template v-if="suggestion">
        <div class="mb-2">
          <b-badge :variant="statusVariant(suggestion.status)" class="mb-1">
            {{ statusLabel(suggestion.status) }}
          </b-badge>
          <h4>
            {{ typeNoun(suggestion.type) }} <b-badge variant="light-primary" style="font-size: 1rem">{{ suggestion.value }}</b-badge>
            {{ typeAction(suggestion.type) }} артисту <strong>{{ suggestion.title }}</strong>
          </h4>
          <div class="text-muted">
            Колонка «{{ typeColumn(suggestion.type) }}» в Notion получит ещё одно значение.
            Создана: {{ formatDateTime(suggestion.created_at) }}
          </div>
        </div>

        <div v-if="artist" class="mb-2">
          <h5>
            Об исполнителе:
            <b-link v-if="$can('view', 'artist')" :to="{ name: 'artist-view', params: { id: artist.id } }">{{ artist.title }}</b-link>
            <span v-else>{{ artist.title }}</span>
          </h5>
          <div class="mb-1">
            <b-badge :variant="artist.russian ? 'light-info' : 'light-primary'" class="mr-50">
              {{ artist.russian ? 'Ру' : 'Зарубеж' }}
            </b-badge>
            <b-badge v-if="artist.is_cover_artist" variant="light-warning">Кавер-группа</b-badge>
          </div>
          <div v-if="artist.tracks.length" class="mb-1">
            <div class="text-muted">Треки (топ-{{ artist.tracks.length }} из {{ artist.tracks_count }}):</div>
            <ol class="pl-2 mb-50">
              <li v-for="track in artist.tracks" :key="track">{{ track }}</li>
            </ol>
          </div>
          <div>
            <div class="text-muted mb-25">Автотопики:</div>
            <template v-if="artist.autotopics.length">
              <b-badge
                  v-for="topic in artist.autotopics"
                  :key="topic"
                  variant="light-secondary"
                  class="mr-50 mb-25"
              >
                {{ topic }}
              </b-badge>
            </template>
            <span v-else class="text-muted">нет</span>
          </div>
        </div>
        <div v-else class="mb-2 text-muted">
          Исполнитель не найден в локальной базе.
        </div>

        <div class="d-flex flex-wrap align-items-center">
          <b-button
              variant="success"
              size="lg"
              class="mr-1 mb-1"
              :disabled="isProcessing || suggestion.status === 'applied'"
              @click="confirm"
          >
            <b-spinner v-if="processingAction === 'confirm'" small class="mr-50" />
            <feather-icon v-else icon="CheckIcon" class="mr-50" />
            Подтвердить
          </b-button>

          <b-button
              variant="outline-secondary"
              class="mr-1 mb-1"
              :disabled="isProcessing || suggestion.status === 'ignored'"
              @click="ignore"
          >
            <b-spinner v-if="processingAction === 'ignore'" small class="mr-50" />
            <feather-icon v-else icon="EyeOffIcon" class="mr-50" />
            Игнорировать
          </b-button>

          <b-button
              variant="outline-danger"
              class="mb-1"
              :disabled="isProcessing"
              @click="remove"
          >
            <b-spinner v-if="processingAction === 'delete'" small class="mr-50" />
            <feather-icon v-else icon="TrashIcon" class="mr-50" />
            Удалить
          </b-button>
        </div>

        <div class="mt-1">
          <b-link :to="{ name: 'content-suggestions' }">← К списку предложек</b-link>
        </div>
      </template>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard,
  BOverlay,
  BButton,
  BBadge,
  BSpinner,
  BLink,
} from 'bootstrap-vue'
import { onUnmounted, ref, computed } from '@vue/composition-api'
import store from '@/store'
import router from '@/router'
import contentStoreModule from '@/views/content/contentStoreModule'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { refreshSuggestionBadge } from '@/views/content/suggestionBadge'

export default {
  components: {
    BCard,
    BOverlay,
    BButton,
    BBadge,
    BSpinner,
    BLink,
  },
  setup() {
    const STORE_MODULE_NAME = 'app-content'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, contentStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()

    const suggestionId = ref(Number(router.currentRoute.params.id))
    const suggestion = ref(null)
    const artist = ref(null)
    const isLoading = ref(false)
    const processingAction = ref(null)
    const isProcessing = computed(() => processingAction.value !== null)

    const statusLabels = { pending: 'Ожидает', applied: 'Применена', ignored: 'Игнорирована' }
    const statusVariants = { pending: 'light-warning', applied: 'light-success', ignored: 'light-secondary' }

    // Как называть значение предложки и в какую Notion-колонку оно применяется — по типу.
    const typeMeta = {
      artist_matrix: { noun: 'Матрица', action: 'будет добавлена', column: 'Матрица' },
      artist_letter_count: { noun: 'Тема', action: 'будет добавлена', column: 'Темы число букв' },
      artist_tag: { noun: 'Тег', action: 'будет добавлен', column: 'Tags' },
      artist_country: { noun: 'Страна', action: 'будет установлена', column: 'Страна' },
      artist_cluster: { noun: 'Кластер', action: 'будет добавлен', column: 'Кластер' },
      artist_first_letter: { noun: 'Буква', action: 'будет установлена', column: 'Первые буквы' },
      artist_ru: { noun: 'Чекбокс Ру', action: 'будет проставлен', column: 'RU' },
    }

    const statusLabel = status => statusLabels[status] || status
    const statusVariant = status => statusVariants[status] || 'light-secondary'
    const typeNoun = type => (typeMeta[type] && typeMeta[type].noun) || 'Значение'
    const typeAction = type => (typeMeta[type] && typeMeta[type].action) || 'будет добавлено'
    const typeColumn = type => (typeMeta[type] && typeMeta[type].column) || type

    const formatDateTime = iso => new Date(iso).toLocaleString('ru-RU', {
      timeZone: 'Europe/Moscow',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

    const showToast = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckCircleIcon',
          variant,
        },
      })
    }

    const errorMessage = (error, fallback) => (
      (error.response && error.response.data && error.response.data.message) || fallback
    )

    const loadSuggestion = () => {
      isLoading.value = true
      store.dispatch('app-content/fetchSuggestion', { id: suggestionId.value })
          .then(response => {
            suggestion.value = response.data.data
            artist.value = response.data.artist
          })
          .catch(error => {
            showToast(errorMessage(error, 'Предложка не найдена'), 'danger')
            router.push({ name: 'content-suggestions' })
          })
          .finally(() => {
            isLoading.value = false
          })
    }

    // После действия: есть следующая pending-предложка — переходим к ней, иначе в список.
    const goNext = nextId => {
      if (nextId) {
        suggestionId.value = nextId
        router.replace({ name: 'content-suggestion-view', params: { id: nextId } })
        loadSuggestion()
      } else {
        showToast('Предложек больше нет', 'success')
        router.push({ name: 'content-suggestions' })
      }
    }

    const confirm = () => {
      processingAction.value = 'confirm'
      store.dispatch('app-content/confirmSuggestion', { id: suggestionId.value })
          .then(response => {
            showToast('Изменение применено к Notion', 'success')
            refreshSuggestionBadge()
            goNext(response.data.next_id)
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка применения к Notion'), 'danger'))
          .finally(() => {
            processingAction.value = null
          })
    }

    const ignore = () => {
      processingAction.value = 'ignore'
      store.dispatch('app-content/ignoreSuggestion', { id: suggestionId.value })
          .then(response => {
            showToast('Предложка игнорирована', 'success')
            refreshSuggestionBadge()
            goNext(response.data.next_id)
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка игнорирования'), 'danger'))
          .finally(() => {
            processingAction.value = null
          })
    }

    const remove = () => {
      processingAction.value = 'delete'
      store.dispatch('app-content/deleteSuggestion', { id: suggestionId.value })
          .then(() => {
            showToast('Предложка удалена', 'success')
            refreshSuggestionBadge()
            router.push({ name: 'content-suggestions' })
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка удаления'), 'danger'))
          .finally(() => {
            processingAction.value = null
          })
    }

    loadSuggestion()

    return {
      suggestion,
      artist,
      isLoading,
      processingAction,
      isProcessing,
      statusLabel,
      statusVariant,
      typeNoun,
      typeAction,
      typeColumn,
      formatDateTime,
      confirm,
      ignore,
      remove,
    }
  },
}
</script>
